import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getDealByIdAsync as getDealById, getDealsAsync as getDefaultDeals, getNacharbeitenStatusAsync as getNacharbeitenStatus, getWartungStatusAsync as getWartungStatus, searchDealsAsync as searchDeals } from '../services/api/hubspot';
import { isAuthMissingError } from '../services/api/one360';
import { HubSpotDeal, WartungStatusSnapshot } from '../types/hubspot';

export type { DealsNacharbeitenFilter, DealsWartungFilter } from '../types/filters';
import type { DealsNacharbeitenFilter, DealsWartungFilter } from '../types/filters';
export type { FetchDealsOptions, DealsState } from '../interfaces/deals';
import type { DealsState } from '../interfaces/deals';

const DEALS_PAGE_SIZE = Number(import.meta.env.VITE_DEALS_PAGE_SIZE || 100);
const DEALS_BACKGROUND_DELAY_MS = 1000;
const DEALS_SEARCH_ENRICH_CONCURRENCY = 4;
const MOBILE_DEALS_HARD_LIMIT = Number(import.meta.env.VITE_MOBILE_DEALS_HARD_LIMIT || 800);
const PERSISTED_DEALS_LIMIT = Number(import.meta.env.VITE_PERSISTED_DEALS_LIMIT || 120);

let backgroundSyncPromise: Promise<void> | null = null;
let backgroundSyncRunId = 0;
// Generation-Token für den initialen fetchDeals()-Request: wird bei jedem Request, der
// einen laufenden anderen Request ablöst, hochgezählt (siehe invalidateFetchRun). Ein
// abgelöster Request erkennt daran nach seinem await, dass er überholt wurde, und darf
// dann keinen State mehr schreiben - unabhängig davon, ob/wann sein AbortSignal greift
// (PR-Review zu #359/#360: signal.abort() allein reicht nicht, falls die Response schon
// eingetroffen ist, bevor der Abort verarbeitet wird).
let activeFetchRunId = 0;
let activeInitialFetchController: AbortController | null = null;
let activeBackgroundPageController: AbortController | null = null;
let activeSearchController: AbortController | null = null;
let searchRunId = 0;
let unfilteredDealsCache: HubSpotDeal[] = [];
let unfilteredHasMoreCache = false;
let unfilteredLastFetchedCache: string | null = null;
let unfilteredAfterCache: string | undefined = undefined;

function invalidateFetchRun() {
  backgroundSyncRunId += 1;
  activeFetchRunId += 1;
  activeBackgroundPageController?.abort();
  activeBackgroundPageController = null;
  activeInitialFetchController?.abort();
  activeInitialFetchController = null;
  // Sofort freigeben, damit ein neuer 100er-Background-Run direkt starten kann.
  backgroundSyncPromise = null;
  // Laufende Such-Enrichments ebenfalls abbrechen, damit Refresh nicht blockiert.
  searchRunId += 1;
  activeSearchController?.abort();
  activeSearchController = null;
}

function isMobileViewport(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 639px)').matches;
}

function trimDealsForDevice(deals: HubSpotDeal[]): HubSpotDeal[] {
  if (!isMobileViewport()) return deals;
  if (deals.length <= MOBILE_DEALS_HARD_LIMIT) return deals;
  return deals.slice(0, MOBILE_DEALS_HARD_LIMIT);
}

function isQuotaExceededError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as any)?.name;
  const code = (error as any)?.code;
  const message = String((error as any)?.message || '').toLowerCase();
  return (
    name === 'QuotaExceededError' ||
    name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    code === 22 ||
    code === 1014 ||
    message.includes('quota') ||
    message.includes('exceeded')
  );
}

function createSafeStorage() {
  const memoryStorage = new Map<string, string>();
  const safeMemory = {
    getItem: (key: string) => memoryStorage.get(key) ?? null,
    setItem: (key: string, value: string) => {
      memoryStorage.set(key, value);
    },
    removeItem: (key: string) => {
      memoryStorage.delete(key);
    },
  };

  if (typeof window === 'undefined' || !window.localStorage) {
    return safeMemory;
  }

  return {
    getItem: (key: string) => {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return memoryStorage.get(key) ?? null;
      }
    },
    setItem: (key: string, value: string) => {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        if (isQuotaExceededError(error)) {
          console.warn('[dealsStore] localStorage quota exceeded, skipping persist write.');
          return;
        }
        console.warn('[dealsStore] localStorage write failed, skipping persist write.', error);
      }
    },
    removeItem: (key: string) => {
      try {
        window.localStorage.removeItem(key);
      } catch {
        memoryStorage.delete(key);
      }
    },
  };
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function mergeDeals(existing: HubSpotDeal[], incoming: HubSpotDeal[]): HubSpotDeal[] {
  const byId = new Map(existing.map((deal) => [deal.id, deal]));
  incoming.forEach((deal) => {
    const current = byId.get(deal.id);
    if (!current) {
      byId.set(deal.id, deal);
      return;
    }

    byId.set(deal.id, {
      ...current,
      ...deal,
      project_status: deal.project_status ?? current.project_status,
    });
  });
  return Array.from(byId.values());
}

function isAbortLikeError(error: unknown): boolean {
  const message = error instanceof Error ? error.message.toLowerCase() : String(error || '').toLowerCase();
  return message.includes('abort') || message.includes('canceled') || message.includes('cancelled');
}

function buildRequestKey(options: {
  query: string;
  stageLabel: string;
  nacharbeiten: DealsNacharbeitenFilter;
  wartung: DealsWartungFilter;
}) {
  return JSON.stringify(options);
}

function localDealMatchesSearch(deal: HubSpotDeal, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    deal.id,
    deal.dealname,
    deal.dealstage_label || deal.dealstage,
    deal.objekt___anschrift,
    deal.deal_owner_name,
    deal.verantwortlicher_monteur_name,
  ]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
  return haystack.includes(q);
}

function isUnfilteredRequest(params: {
  query: string;
  stageLabel: string;
  nacharbeiten: DealsNacharbeitenFilter;
  wartung: DealsWartungFilter;
}): boolean {
  return (
    params.query.length === 0 &&
    params.stageLabel.length === 0 &&
    params.nacharbeiten === 'all' &&
    params.wartung === 'all'
  );
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];
  const normalizedConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from({ length: normalizedConcurrency }, async () => {
    while (true) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      if (currentIndex >= items.length) return;
      results[currentIndex] = await mapper(items[currentIndex]);
    }
  });

  await Promise.all(workers);
  return results;
}

function injectNacharbeiten(deal: HubSpotDeal, nacharbeiten: any): HubSpotDeal {
  if (!nacharbeiten) return deal;
  if (!deal.project_status) return deal;
  return {
    ...deal,
    project_status: {
      ...deal.project_status,
      nacharbeiten,
    },
  };
}

function injectWartung(deal: HubSpotDeal, wartung: WartungStatusSnapshot | null): HubSpotDeal {
  if (!wartung) return deal;
  if (!deal.project_status) return deal;
  return {
    ...deal,
    project_status: {
      ...deal.project_status,
      wartung,
    },
  };
}

async function enrichDealsWithStatus(
  deals: HubSpotDeal[],
  signal?: AbortSignal
): Promise<HubSpotDeal[]> {
  if (deals.length === 0) return deals;

  const enriched = await mapWithConcurrency(deals, DEALS_SEARCH_ENRICH_CONCURRENCY, async (deal) => {
    if (signal?.aborted) return deal;

    const [freshDeal, nacharbeiten, wartung] = await Promise.all([
      getDealById(deal.id).catch(() => null),
      getNacharbeitenStatus(deal.id).catch(() => null),
      getWartungStatus(deal.id).catch(() => null),
    ]);

    const merged = freshDeal ? mergeDeals([deal], [freshDeal])[0] : deal;
    return injectWartung(injectNacharbeiten(merged, nacharbeiten), wartung);
  });

  return enriched;
}

export const useDealsStore = create<DealsState>()(
  persist(
    (set, get) => ({
      deals: [],
      searchResults: null,
      lastFetched: null,
      afterCursor: null,
      cacheOwnerKey: null,
      activeRequestKey: null,
      isLoading: false,
      isLoadingMore: false,
      hasMore: false,
      error: null,
      loadMoreError: null,
      isSearching: false,
      searchError: null,

      fetchDeals: async (options = {}) => {
        const query = (options.query || '').trim();
        const stageLabel = (options.stageLabel || '').trim();
        const nacharbeiten = options.nacharbeiten || 'all';
        const wartung = options.wartung || 'all';
        const requestKey = buildRequestKey({ query, stageLabel, nacharbeiten, wartung });
        const requestIsUnfiltered = isUnfilteredRequest({ query, stageLabel, nacharbeiten, wartung });

        const startBackgroundSync = (
          params: { query: string; stageLabel: string; nacharbeiten: DealsNacharbeitenFilter; wartung: DealsWartungFilter },
          initialAfter?: string,
          bypassCache?: boolean
        ) => {
          if (!initialAfter) return;
          if (backgroundSyncPromise) return;
          const runId = backgroundSyncRunId;
          backgroundSyncPromise = (async () => {
            let nextAfterCursor: string | undefined = initialAfter;
            try {
              while (nextAfterCursor && (get().hasMore)) {
                if (runId !== backgroundSyncRunId) break;
                if (!navigator.onLine) break;
                await delay(DEALS_BACKGROUND_DELAY_MS);
                if (runId !== backgroundSyncRunId) break;
                set({ isLoadingMore: true });
                activeBackgroundPageController?.abort();
                activeBackgroundPageController = new AbortController();
                const nextPage = await getDefaultDeals({
                  after: nextAfterCursor,
                  bypassCache,
                  limit: DEALS_PAGE_SIZE,
                  q: params.query || undefined,
                  stageLabel: params.stageLabel || undefined,
                  nacharbeiten: params.nacharbeiten,
                  wartung: params.wartung,
                  signal: activeBackgroundPageController.signal,
                });
                // Ein neuerer Fetch/Refresh hat diesen Background-Run zwischenzeitlich
                // abgelöst (siehe invalidateFetchRun) - nicht mehr mergen, sonst schreibt
                // die verspätete Antwort stale Deals/Cursor/hasMore in den Store (PR-Review
                // zu #359/#360: Abort allein reicht nicht, falls die Response schon da ist).
                if (runId !== backgroundSyncRunId) break;
                const nextDeals = nextPage.deals || [];
                nextAfterCursor = nextPage.after || undefined;
                set((state) => {
                  const mergedDeals = mergeDeals(state.deals, nextDeals);
                  const trimmedDeals = trimDealsForDevice(mergedDeals);
                  const reachedDeviceLimit = trimmedDeals.length < mergedDeals.length;
                  const nextHasMore = !reachedDeviceLimit && Boolean(nextPage.hasMore) && !!nextPage.after;
                  const nowIso = new Date().toISOString();

                  if (isUnfilteredRequest(params)) {
                    unfilteredDealsCache = trimmedDeals;
                    unfilteredHasMoreCache = nextHasMore;
                    unfilteredLastFetchedCache = nowIso;
                    unfilteredAfterCache = nextAfterCursor;
                  }

                  return {
                    deals: trimmedDeals,
                    hasMore: nextHasMore,
                    afterCursor: nextAfterCursor || null,
                    lastFetched: nowIso,
                    isLoadingMore: false,
                  };
                });
                if (!nextAfterCursor || nextDeals.length === 0) break;
              }
            } catch (err: any) {
              if (runId !== backgroundSyncRunId) return;
              if (isAbortLikeError(err) || isAuthMissingError(err)) {
                set({ isLoadingMore: false });
                return;
              }
              set({
                loadMoreError: err.message || 'Weitere Deals konnten nicht nachgeladen werden.',
                isLoadingMore: false,
              });
            } finally {
              activeBackgroundPageController = null;
              backgroundSyncPromise = null;
            }
          })();
        };

        const { deals, lastFetched, isLoading, cacheOwnerKey: existingCacheOwnerKey, activeRequestKey } = get();

        const nextCacheOwnerKey = options.cacheOwnerKey || existingCacheOwnerKey || null;
        const sameOwner =
          !existingCacheOwnerKey ||
          !nextCacheOwnerKey ||
          existingCacheOwnerKey === nextCacheOwnerKey;
        const sameRequest = !activeRequestKey || activeRequestKey === requestKey;

        // Diese Wiederherstellung aus dem In-Memory-Cache läuft bewusst VOR dem isLoading-Guard:
        // Sie greift rein lesend auf unfilteredDealsCache zu und setzt keinen neuen Request auf,
        // darf also nicht durch einen parallel laufenden Fetch (z.B. Recovery-Fetch beim
        // Verlassen der Detailseite) blockiert werden - sonst bleibt die Liste beim
        // Zurücknavigieren fälschlich auf einen einzelnen Deal reduziert.
        if (options.restoreUnfilteredFromCache && requestIsUnfiltered && unfilteredDealsCache.length > 0) {
          // Einen noch laufenden Initial-Fetch abbrechen: dessen "then"-Zweig würde sonst
          // verspätet (ggf. mit Ergebnissen eines anderen/gefilterten Requests) erneut
          // deals/activeRequestKey/isLoading setzen und den gerade wiederhergestellten
          // Cache-Stand wieder überschreiben (siehe PR-Review-Kommentar zu #300).
          if (isLoading) {
            invalidateFetchRun();
          }
          const cursor = unfilteredAfterCache || get().afterCursor || undefined;
          set({
            deals: unfilteredDealsCache,
            hasMore: unfilteredHasMoreCache,
            lastFetched: unfilteredLastFetchedCache || new Date().toISOString(),
            cacheOwnerKey: nextCacheOwnerKey,
            activeRequestKey: requestKey,
            isLoading: false,
            isLoadingMore: false,
            error: null,
            loadMoreError: null,
          });
          if (unfilteredHasMoreCache) {
            startBackgroundSync({ query, stageLabel, nacharbeiten, wartung }, cursor);
          }
          return;
        }

        // Läuft bereits ein Fetch für exakt denselben Request (z.B. doppelt gefeuerter Effect),
        // reicht Warten statt Doppel-Request. Weicht der neue Request ab (z.B. Deal-Status-Filter
        // während des initialen ungefilterten Ladevorgangs geändert), MUSS er trotzdem durchlaufen -
        // sonst wird der Filter-Request stillschweigend verworfen und es bleibt beim alten
        // (ungefilterten) Ergebnis, obwohl der Nutzer längst einen anderen Status ausgewählt hat.
        if (isLoading && sameRequest && sameOwner) return;

        const storedCursor = unfilteredAfterCache || get().afterCursor || undefined;
        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        const canUseCachedDeals = isOffline
          && !options.forceRefresh && !options.refresh && !options.refreshNacharbeiten && !options.refreshWartung
          && sameOwner && sameRequest && deals.length > 0 && lastFetched
          && (!get().hasMore || !!storedCursor);
        if (canUseCachedDeals) {
          set({
            cacheOwnerKey: nextCacheOwnerKey,
            activeRequestKey: requestKey,
            error: null,
            loadMoreError: null,
          });
          return;
        }

        invalidateFetchRun();
        // Generation dieses Requests festhalten: Läuft währenddessen ein NEUER Request
        // an (der wiederum invalidateFetchRun() aufruft und activeFetchRunId erhöht),
        // erkennt dieser Request das nach seinem await und darf dann keinen State mehr
        // schreiben - auch wenn sein AbortSignal (noch) nicht gegriffen hat.
        const runId = activeFetchRunId;

        // Besitzerwechsel (z.B. anderer Google-Account auf demselben Gerät) sofort
        // aus der Anzeige nehmen, statt bis zum Abschluss des neuen Fetches die alten
        // (fremden) Deals weiter anzuzeigen - sameRequest allein deckt das nicht ab,
        // wenn Filter/Query zufällig identisch sind.
        if (options.forceRefresh || !sameRequest || !sameOwner) {
          set({
            deals: [],
            searchResults: null,
            hasMore: false,
            lastFetched: null,
            error: null,
            loadMoreError: null,
          });
        }

        set({
          isLoading: true,
          isLoadingMore: false,
          activeRequestKey: requestKey,
          error: null,
          loadMoreError: null,
        });
        const myController = new AbortController();
        try {
          activeInitialFetchController = myController;
          const data = await getDefaultDeals({
            refresh: options.refresh || options.forceRefresh,
            refreshNacharbeiten: options.refreshNacharbeiten,
            refreshWartung: options.refreshWartung,
            limit: DEALS_PAGE_SIZE,
            q: query || undefined,
            stageLabel: stageLabel || undefined,
            nacharbeiten,
            wartung,
            signal: options.signal || myController.signal,
          });
          // Ein neuerer Request hat diesen hier zwischenzeitlich abgelöst (siehe runId
          // oben) - nicht mehr in den Store schreiben, sonst überschreibt die verspätete
          // Antwort dieses alten Requests den bereits aktuelleren Zustand.
          if (runId !== activeFetchRunId) return;

          const firstPageDeals = data.deals || [];
          const trimmedFirstPageDeals = trimDealsForDevice(firstPageDeals);
          const reachedDeviceLimit = trimmedFirstPageDeals.length < firstPageDeals.length;
          const nowIso = new Date().toISOString();

          if (requestIsUnfiltered) {
            unfilteredDealsCache = trimmedFirstPageDeals;
            unfilteredHasMoreCache = !reachedDeviceLimit && Boolean(data.hasMore);
            unfilteredLastFetchedCache = nowIso;
            unfilteredAfterCache = data.after || undefined;
          }

          set(() => ({
            deals: trimmedFirstPageDeals,
            searchResults: query.length >= 3 ? trimmedFirstPageDeals : null,
            lastFetched: nowIso,
            afterCursor: data.after || null,
            cacheOwnerKey: nextCacheOwnerKey,
            activeRequestKey: requestKey,
            hasMore: !reachedDeviceLimit && Boolean(data.hasMore),
            isLoading: false,
          }));
          if (data.hasMore && !reachedDeviceLimit) {
            const isRefresh = !!(options.refresh || options.forceRefresh);
            startBackgroundSync({ query, stageLabel, nacharbeiten, wartung }, data.after || undefined, isRefresh);
          }
        } catch (err: any) {
          if (runId !== activeFetchRunId) return;
          if (isAbortLikeError(err) || isAuthMissingError(err)) {
            set({
              isLoading: false,
              isLoadingMore: false,
            });
            return;
          }
          set({
            error: err.message || 'Fehler beim Laden der Deals',
            isLoading: false,
            isLoadingMore: false,
          });
        } finally {
          if (activeInitialFetchController === myController) {
            activeInitialFetchController = null;
          }
        }
      },

      searchDealsIntoCache: async (query: string, options = {}) => {
        void options;
        const q = query.trim();
        if (q.length < 3) return;

        searchRunId += 1;
        const currentSearchRunId = searchRunId;
        activeSearchController?.abort();
        activeSearchController = new AbortController();

        const currentDeals = get().deals;
        const localMatches = currentDeals.filter((deal) => localDealMatchesSearch(deal, q));

        set({ isSearching: true, searchError: null });
        set({
          searchResults: localMatches,
          hasMore: false,
          activeRequestKey: buildRequestKey({ query: q, stageLabel: '', nacharbeiten: 'all', wartung: 'all' }),
        });

        // One360-Server-Suche sofort parallel zum lokalen Enrichment anstoßen (ADO #1196):
        // vorher lief sie erst NACH dem Enrichment der lokalen Treffer (bis zu 3 API-Calls
        // pro Treffer), was die eigentlich relevante Server-Suche unnötig verzögerte, obwohl
        // der lokale Cache ohnehin nur einen unvollständigen Ausschnitt aller Deals hält.
        const serverSearchPromise = searchDeals(q, { signal: activeSearchController.signal }).then((matches) => {
          if (currentSearchRunId === searchRunId) {
            // Rohtreffer sofort anzeigen statt auf ihr eigenes Enrichment zu warten.
            set((state) => ({ searchResults: mergeDeals(state.searchResults || [], matches || []) }));
          }
          return matches;
        });
        // Verhindert eine Unhandled Promise Rejection: der Guard weiter unten kann die Funktion
        // verlassen (neuerer Suchlauf/invalidateFetchRun während des lokalen Enrichments), BEVOR
        // "await serverSearchPromise" erreicht wird. Die eigentliche Fehlerbehandlung bleibt
        // unverändert bei diesem späteren await/catch - dieser No-Op-Handler fängt nur die sonst
        // ungehandelte Rejection ab, falls er nie erreicht wird (Code-Review-Finding zu PR #418/#419).
        serverSearchPromise.catch(() => {});

        try {
          const enrichedLocal = await enrichDealsWithStatus(localMatches, activeSearchController.signal);
          if (currentSearchRunId !== searchRunId) return;
          set((state) => ({
            searchResults: mergeDeals(state.searchResults || [], enrichedLocal),
            deals: mergeDeals(state.deals, enrichedLocal),
          }));

          const serverMatches = await serverSearchPromise;
          if (currentSearchRunId !== searchRunId) return;
          const serverEnriched = await enrichDealsWithStatus(serverMatches || [], activeSearchController.signal);
          if (currentSearchRunId !== searchRunId) return;
          const merged = mergeDeals(enrichedLocal, serverEnriched || []);
          set({
            searchResults: merged,
            deals: mergeDeals(get().deals, merged),
            lastFetched: new Date().toISOString(),
            hasMore: false,
            isSearching: false,
          });
        } catch (err: any) {
          if (isAbortLikeError(err) || isAuthMissingError(err)) {
            if (currentSearchRunId === searchRunId) {
              set({ isSearching: false });
            }
            return;
          }
          if (currentSearchRunId !== searchRunId) return;
          set({
            searchError: err.message || 'HubSpot-Suche fehlgeschlagen.',
            isSearching: false,
          });
        } finally {
          if (currentSearchRunId === searchRunId) {
            activeSearchController = null;
          }
        }
      },

      upsertDeal: (deal: HubSpotDeal) => {
        // Nur mergen, wenn unfilteredDealsCache bereits eine ECHTE Liste aus einem vorherigen
        // fetchDeals()-Aufruf dieser Session enthält. Landet man z.B. per Deep-Link/PWA-Resume
        // direkt auf einer Deal-Detailseite, ohne dass zuvor die ungefilterte Liste geladen
        // wurde, ist unfilteredDealsCache noch leer ([]) — ein Merge würde ihn dann auf genau
        // 1 Eintrag (diesen einen Deal) "poisonen". restoreUnfilteredFromCache prüft beim
        // Zurücknavigieren nur `.length > 0`, würde diese 1-Deal-Liste also fälschlich als
        // vollständige Deal-Liste wiederherstellen, statt einen echten Fetch auszulösen — die
        // Liste bliebe dann dauerhaft auf den einen zuletzt geöffneten Deal reduziert.
        if (unfilteredDealsCache.length > 0) {
          unfilteredDealsCache = mergeDeals(unfilteredDealsCache, [deal]);
          unfilteredLastFetchedCache = new Date().toISOString();
        }
        set((state) => ({
          deals: mergeDeals(state.deals, [deal]),
          searchResults: state.searchResults ? mergeDeals(state.searchResults, [deal]) : state.searchResults,
          lastFetched: new Date().toISOString(),
        }));
      },

      clearLoadMoreError: () => set({ loadMoreError: null }),

      // Bei Logout/falschem Konto/ungültiger Session aufrufen: der Store persistiert
      // Deals in localStorage unabhängig vom Auth-Zustand - ohne diesen Reset würden
      // die Deals eines vorherigen (ggf. anderen) Google-Kontos für jeden sichtbar
      // bleiben, der die App danach öffnet, auch wenn der Nachlade-Versuch mangels
      // Token fehlschlägt. Löscht auch die modul-internen In-Memory-Caches
      // (unfilteredDealsCache etc.), sonst würde restoreUnfilteredFromCache die alten
      // Deals wieder aus dem Speicher zurückholen.
      clearDeals: () => {
        invalidateFetchRun();
        unfilteredDealsCache = [];
        unfilteredHasMoreCache = false;
        unfilteredLastFetchedCache = null;
        unfilteredAfterCache = undefined;
        set({
          deals: [],
          searchResults: null,
          lastFetched: null,
          afterCursor: null,
          cacheOwnerKey: null,
          activeRequestKey: null,
          isLoading: false,
          isLoadingMore: false,
          hasMore: false,
          error: null,
          loadMoreError: null,
          isSearching: false,
          searchError: null,
        });
      },
    }),
    {
      name: '360volt-deals-store',
      version: 2,
      storage: createJSONStorage(createSafeStorage),
      partialize: (state) => ({
        deals: state.deals.slice(0, PERSISTED_DEALS_LIMIT),
        lastFetched: state.lastFetched,
        cacheOwnerKey: state.cacheOwnerKey,
        hasMore: state.hasMore,
        activeRequestKey: state.activeRequestKey,
        afterCursor: state.afterCursor,
      }),
    }
  )
);
